import { z } from "zod";

/**
 * The slice of an RTK Query error we care about (the `error` field of a query
 * hook, or the rejection of a mutation `unwrap()`): the HTTP status, and the
 * `errorCode` the API may include in its JSON body (via `include_in_json`).
 * A non-JSON or codeless body parses to `data: null`.
 */
const queryErrorSchema = z.object({
  status: z.number(),
  data: z.object({ errorCode: z.string() }).nullish().catch(null),
});

function parseQueryError(
  error: unknown,
): z.infer<typeof queryErrorSchema> | null {
  const result = queryErrorSchema.safeParse(error);
  return result.success ? result.data : null;
}

export function isUnauthorizedQueryError(error: unknown): boolean {
  return parseQueryError(error)?.status === 401;
}

export function isConflictQueryError(error: unknown): boolean {
  return parseQueryError(error)?.status === 409;
}

export function isForbiddenQueryError(error: unknown): boolean {
  return parseQueryError(error)?.status === 403;
}

/**
 * Reads the `errorCode` an API error response carried in its JSON body, if
 * any.
 */
export function readQueryErrorCode(error: unknown): string | null {
  return parseQueryError(error)?.data?.errorCode ?? null;
}
